import { BreathworkConfiguration } from './BreathworkConfiguration';

// ---------------------------------------------------------------------------
// MinMaxInt
// ---------------------------------------------------------------------------

export interface MinMaxInt {
  min: number;
  defaultValue: number;
  max: number;
}

export function minMaxIntFromJson(json: Record<string, unknown>): MinMaxInt {
  return {
    min: json['min'] as number,
    defaultValue: json['default'] as number,
    max: json['max'] as number,
  };
}

// ---------------------------------------------------------------------------
// BreathStage
// ---------------------------------------------------------------------------

export enum BreathStage {
  inhale = 'INHALE',
  exhale = 'EXHALE',
  fullRetention = 'FULL_LUNGS_RETENTION',
  emptyRetention = 'EMPTY_LUNGS_RETENTION',
}

export function breathStageFromId(id: string): BreathStage {
  const found = Object.values(BreathStage).find((v) => v === id);
  if (!found) throw new Error(`Unknown BreathStage: ${id}`);
  return found;
}

// ---------------------------------------------------------------------------
// Comparison
// ---------------------------------------------------------------------------

export interface Comparison {
  isEqual?: boolean | null;
  isGreater?: boolean | null;
  isMultiplied?: number | null;
}

export function comparisonFromJson(json: Record<string, unknown>): Comparison {
  return {
    isEqual: (json['isEqual'] as boolean | null) ?? null,
    isGreater: (json['isGreater'] as boolean | null) ?? null,
    isMultiplied: (json['isMultiplied'] as number | null) ?? null,
  };
}

// ---------------------------------------------------------------------------
// BreathworkRule
// ---------------------------------------------------------------------------

export interface BreathworkRule {
  firstBreathStage: BreathStage;
  secondBreathStage: BreathStage;
  comparison: Comparison;
  message: string;
}

export function breathworkRuleFromJson(json: Record<string, unknown>): BreathworkRule {
  return {
    firstBreathStage: breathStageFromId(json['firstBreathStage'] as string),
    secondBreathStage: breathStageFromId(json['secondBreathStage'] as string),
    comparison: comparisonFromJson(json['comparison'] as Record<string, unknown>),
    message: json['message'] as string,
  };
}

function getDurationForStage(stage: BreathStage, config: BreathworkConfiguration): number {
  switch (stage) {
    case BreathStage.inhale: return config.breathDuration.inhale;
    case BreathStage.fullRetention: return config.breathDuration.fullLungsRetention;
    case BreathStage.exhale: return config.breathDuration.exhale;
    case BreathStage.emptyRetention: return config.breathDuration.emptyLungsRetention;
  }
}

/** Throws with rule.message if the rule is violated. */
export function validateBreathworkRule(rule: BreathworkRule, config: BreathworkConfiguration): void {
  if (rule.firstBreathStage === rule.secondBreathStage) {
    throw new Error('Provided the same arguments, nothing to compare.');
  }
  const first = getDurationForStage(rule.firstBreathStage, config);
  const second = getDurationForStage(rule.secondBreathStage, config);

  if (rule.comparison.isEqual === true && first !== second) throw new Error(rule.message);
  if (rule.comparison.isEqual === false && first === second) throw new Error(rule.message);
  if (rule.comparison.isGreater === true && first <= second) throw new Error(rule.message);
  if (rule.comparison.isGreater === false && first >= second) throw new Error(rule.message);
}

export function isBreathworkRuleValid(rule: BreathworkRule, config: BreathworkConfiguration): boolean {
  try {
    validateBreathworkRule(rule, config);
    return true;
  } catch {
    return false;
  }
}

// ---------------------------------------------------------------------------
// BreathworkConfigurationUi
// ---------------------------------------------------------------------------

export interface BreathworkConfigurationUi {
  numberOfRounds: MinMaxInt;
  numberOfBreathsOnFirstRound?: MinMaxInt | null;
  allowIncrementBetweenRounds?: boolean | null;
  allowFullLungsRetentionOnRoundEnd?: boolean | null;
  fullLungsRetentionOnRoundEnd?: MinMaxInt | null;
  allowFullLungsRetentionOnBreathworkEnd?: boolean | null;
  fullLungsRetentionOnBreathworkEnd?: MinMaxInt | null;
  inhaleDurationInSec?: MinMaxInt | null;
  fullLungsRetentionDurationInSec?: MinMaxInt | null;
  exhaleDurationInSec?: MinMaxInt | null;
  emptyLungsRetentionDurationInSec?: MinMaxInt | null;
  allBreathingStagesDurationsInSec?: MinMaxInt | null;
  inhaleExhaleStagesDurationsInSec?: MinMaxInt | null;
  isBreathworkRatioSupported: boolean;
  rules?: BreathworkRule[] | null;
}

export function breathworkConfigurationUiFromJson(json: Record<string, unknown>): BreathworkConfigurationUi {
  const opt = (key: string) =>
    json[key] != null ? minMaxIntFromJson(json[key] as Record<string, unknown>) : null;
  return {
    numberOfRounds: minMaxIntFromJson(json['numberOfRounds'] as Record<string, unknown>),
    numberOfBreathsOnFirstRound: opt('numberOfBreathsOnFirstRound'),
    allowIncrementBetweenRounds: (json['allowIncrementBetweenRounds'] as boolean | null) ?? null,
    allowFullLungsRetentionOnRoundEnd: (json['allowFullLungsRetentionOnRoundEnd'] as boolean | null) ?? null,
    fullLungsRetentionOnRoundEnd: opt('fullLungsRetentionOnRoundEnd'),
    allowFullLungsRetentionOnBreathworkEnd: (json['allowFullLungsRetentionOnBreathworkEnd'] as boolean | null) ?? null,
    fullLungsRetentionOnBreathworkEnd: opt('fullLungsRetentionOnBreathworkEnd'),
    inhaleDurationInSec: opt('inhaleDurationInSec'),
    fullLungsRetentionDurationInSec: opt('fullLungsRetentionDurationInSec'),
    exhaleDurationInSec: opt('exhaleDurationInSec'),
    emptyLungsRetentionDurationInSec: opt('emptyLungsRetentionDurationInSec'),
    allBreathingStagesDurationsInSec: opt('allBreathingStagesDurationsInSec'),
    inhaleExhaleStagesDurationsInSec: opt('inhaleExhaleStagesDurationsInSec'),
    isBreathworkRatioSupported: json['isBreathworkRatioSupported'] as boolean,
    rules: json['rules'] != null
      ? (json['rules'] as Record<string, unknown>[]).map(breathworkRuleFromJson)
      : null,
  };
}

// ---------------------------------------------------------------------------
// Static estimation helpers (ported from BreathworkConfigurationUi static methods)
// All durations are in milliseconds.
// ---------------------------------------------------------------------------

export function calculateNumberOfBreathsInRound(params: {
  round: number;
  breathsOnFirstRound?: number | null;
  incrementBetweenRounds?: boolean | null;
}): number {
  const { round, breathsOnFirstRound, incrementBetweenRounds } = params;
  if (breathsOnFirstRound == null) return 1;
  if (incrementBetweenRounds === true) return breathsOnFirstRound + (round - 1) * breathsOnFirstRound;
  return breathsOnFirstRound;
}

export function estimateDurationMs(params: {
  id: string;
  numberOfRounds: number | null;
  numberOfBreathsOnFirstRound?: number | null;
  incrementBetweenRounds: boolean;
  allowFullLungsRetentionOnRoundEnd?: boolean | null;
  allowFullLungsRetentionOnBreathworkEnd?: boolean | null;
  inhaleMs: number | null;
  fullLungsRetentionMs?: number | null;
  exhaleMs: number | null;
  emptyLungsRetentionMs?: number | null;
}): number | null {
  const {
    id,
    numberOfRounds,
    numberOfBreathsOnFirstRound,
    incrementBetweenRounds,
    allowFullLungsRetentionOnRoundEnd,
    allowFullLungsRetentionOnBreathworkEnd,
  } = params;

  if (numberOfRounds == null || numberOfRounds <= 0) return null;

  let inhaleMs: number | null;
  let fullLungsMs: number | null;
  let exhaleMs: number | null;
  let emptyLungsMs: number | null;

  if (id === 'BHASTRIKA') {
    inhaleMs = 250; fullLungsMs = 0; exhaleMs = 250; emptyLungsMs = 0;
  } else if (id === 'KAPALABHATI') {
    inhaleMs = 300; fullLungsMs = 0; exhaleMs = 250; emptyLungsMs = 0;
  } else if (id === 'KUMBHAKA') {
    if (params.inhaleMs == null) return null;
    inhaleMs = params.inhaleMs;
    fullLungsMs = params.fullLungsRetentionMs ?? null;
    exhaleMs = params.inhaleMs * 2;
    emptyLungsMs = params.emptyLungsRetentionMs ?? null;
  } else if (id === 'TUMMO') {
    if (params.inhaleMs == null) return null;
    inhaleMs = params.inhaleMs;
    fullLungsMs = params.fullLungsRetentionMs ?? null;
    exhaleMs = 500;
    emptyLungsMs = params.emptyLungsRetentionMs ?? null;
  } else if (id === '4_7_8') {
    inhaleMs = 4000; fullLungsMs = 7000; exhaleMs = 8000; emptyLungsMs = 0;
  } else {
    inhaleMs = params.inhaleMs;
    fullLungsMs = params.fullLungsRetentionMs ?? null;
    exhaleMs = params.exhaleMs;
    emptyLungsMs = params.emptyLungsRetentionMs ?? null;
  }

  if (inhaleMs == null || exhaleMs == null) return null;

  const breathMs = inhaleMs + (fullLungsMs ?? 0) + exhaleMs + (emptyLungsMs ?? 0);
  let durationMs = 0;

  for (let i = 1; i <= numberOfRounds; i++) {
    const breathsInRound = calculateNumberOfBreathsInRound({
      round: i,
      breathsOnFirstRound: numberOfBreathsOnFirstRound,
      incrementBetweenRounds,
    });
    durationMs += breathMs * breathsInRound;

    if (allowFullLungsRetentionOnRoundEnd === true) durationMs += 28_000;

    if (numberOfBreathsOnFirstRound != null && i < numberOfRounds) {
      durationMs += 10_000; // inter-round prep
    }
  }

  if (allowFullLungsRetentionOnBreathworkEnd === true) durationMs += 28_000;
  durationMs += 10_000; // breathwork closing

  return durationMs;
}

export function getAcceptableNumberOfRounds(params: {
  id: string;
  numberOfBreathsOnFirstRound?: number | null;
  incrementBetweenRounds: boolean;
  allowFullLungsRetentionOnRoundEnd?: boolean | null;
  allowFullLungsRetentionOnBreathworkEnd?: boolean | null;
  inhaleMs: number | null;
  fullLungsRetentionMs?: number | null;
  exhaleMs: number | null;
  emptyLungsRetentionMs?: number | null;
  minDurationMs: number;
  maxDurationMs: number;
  maxRoundsToCheck?: number;
}): number[] {
  const { minDurationMs, maxDurationMs, maxRoundsToCheck = 150 } = params;
  const valid: number[] = [];
  for (let rounds = 1; rounds <= maxRoundsToCheck; rounds++) {
    const duration = estimateDurationMs({ ...params, numberOfRounds: rounds });
    if (duration != null && duration >= minDurationMs && duration <= maxDurationMs) {
      valid.push(rounds);
    }
  }
  return valid;
}
